import React, { useMemo, useState } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Globe,
  AlertCircle,
  Loader2,
  ListChecks,
} from 'lucide-react';
import { Button, CustomSelect, PillToggle } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import CreateCountryModal from '../../components/admin/CreateCountryModal';
import { useToastContext } from "../../contexts/ToastContext";

import { 
  useAdminCountries, 
  useCreateCountry, 
  useUpdateCountry, 
  useDeleteCountry, 
  useToggleCountryStatus
} from '../../services/countryService';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

/** Only these countries may be active on the marketplace. */
const ENABLED_COUNTRY_CODES = new Set(['GH', 'US', 'CA']);

const STATUS_FILTER_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'all', label: 'All' },
];

const AdminCountries = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active');
  
  const { success, error: showError } = useToastContext();

  // API query parameters - no pagination since DataTable handles it
  const queryParams = {
    sort_by: 'sort_order',
    sort_order: 'asc'
  };

  // Fetch countries from API
  const { 
    data: countriesData, 
    isLoading: loading, 
    error: countriesError,
    refetch 
  } = useAdminCountries(queryParams);
  const countries = countriesData?.data || [];

  const filteredCountries = useMemo(() => {
    if (statusFilter === 'active') return countries.filter((c) => Boolean(c.is_active));
    if (statusFilter === 'inactive') return countries.filter((c) => !c.is_active);
    return countries;
  }, [countries, statusFilter]);

  // API mutations
  const createCountryMutation = useCreateCountry({
    onSuccess: () => {
      success('Country created successfully', 'success');
      setShowCreateModal(false);
    },
    onError: (error) => {
      showError(error.message || 'Failed to create country', 'error');
    }
  });

  const updateCountryMutation = useUpdateCountry({
    onSuccess: () => {
      success('Country updated successfully', 'success');
      setShowEditModal(false);
      setSelectedCountry(null);
    },
    onError: (error) => {
      showError(error.message || 'Failed to update country', 'error');
    }
  });

  const deleteCountryMutation = useDeleteCountry({
    onSuccess: () => {
      success('Country deleted successfully', 'success');
    },
    onError: (error) => {
      showError(error.message || 'Failed to delete country', 'error');
    }
  });

  const toggleStatusMutation = useToggleCountryStatus({
    onSuccess: () => {
      success('Country status updated successfully');
    },
    onError: (error) => {
      showError(error.message || 'Failed to update country status');
    }
  });

  const isStatusToggling = (countryId) =>
    toggleStatusMutation.isPending && toggleStatusMutation.variables === countryId;

  const handleToggleStatus = (country) => {
    const code = String(country?.code || '').toUpperCase();
    if (!ENABLED_COUNTRY_CODES.has(code) && !country?.is_active) {
      showError('Only Ghana, United States, and Canada can be enabled.');
      return;
    }
    toggleStatusMutation.mutate(country.id);
  };

  const handleDeleteCountry = (countryId) => {
    if (window.confirm('Are you sure you want to delete this country?')) {
      deleteCountryMutation.mutate(countryId);
    }
  };

  const openEditModal = (country) => {
    setSelectedCountry(country);
    setShowEditModal(true);
  };

  // Define columns for DataTable
  const columns = [
    {
      key: 'name',
      label: 'Country',
      sortable: true,
      searchable: true,
      render: (country) => (
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10">
            <div className="h-10 w-10 rounded-full bg-gray-900 dark:bg-gray-100 flex items-center justify-center">
              {country.emoji ? (
                <span className="text-lg">{country.emoji}</span>
              ) : (
                <Globe className="h-5 w-5 text-white dark:text-gray-900" />
              )}
            </div>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-50">
              {country.name}
            </div>
            {country.unicode && (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Unicode: {country.unicode}
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      searchable: true,
      render: (country) => (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300">
          {country.code}
        </span>
      )
    },
    {
      key: 'dial_code',
      label: 'Dial Code',
      sortable: true,
      searchable: true,
      render: (country) => country.dial_code || '-'
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      render: (country) => {
        const code = String(country.code || '').toUpperCase();
        const canEnable = ENABLED_COUNTRY_CODES.has(code);
        return (
          <PillToggle
            checked={Boolean(country.is_active)}
            onChange={() => handleToggleStatus(country)}
            loading={isStatusToggling(country.id)}
            disabled={!canEnable && !country.is_active}
            variant="success"
            onLabel="On"
            offLabel="Off"
            ariaLabel={`${country.name} active status`}
          />
        );
      },
    },
    {
      key: 'sort_order',
      label: 'Sort Order',
      sortable: true,
      render: (country) => country.sort_order
    }
  ];

  const actions = [
    {
      label: 'Edit',
      icon: Edit,
      onClick: (country) => openEditModal(country),
      title: 'Edit Country',
      variant: 'default',
    },
    {
      label: 'Delete',
      icon: Trash2,
      onClick: (country) => handleDeleteCountry(country.id),
      title: 'Delete Country',
      variant: 'danger',
    },
  ];

  const handleCreateCountry = (countryData) => {
    createCountryMutation.mutate(countryData);
  };

  const handleEditCountry = (countryData) => {
    updateCountryMutation.mutate({
      id: selectedCountry.id,
      data: countryData
    });
  };

  if (countriesError) {
    return (
      <div className="space-y-5">
        <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-red-50/80 dark:bg-red-950/30 p-8 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <h3 className="text-lg font-medium text-red-800 dark:text-red-200 mb-2">Error Loading Countries</h3>
          <p className="text-red-600 dark:text-red-300 text-sm mb-6">
            {countriesError.message || 'Failed to load countries'}
          </p>
          <Button className="!rounded-xl" onClick={() => refetch()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Countries"
        subtitle="Only Ghana, United States, and Canada can be enabled."
        actions={
          <Button
            onClick={() => setShowCreateModal(true)}
            className="!rounded-xl flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Country
          </Button>
        }
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.countries.title}
        steps={ADMIN_PAGE_GUIDES.countries.steps}
        icon={ListChecks}
      />

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5 space-y-4">
          <div className="max-w-xs">
            <CustomSelect
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={STATUS_FILTER_OPTIONS}
              size="sm"
            />
          </div>

          {loading ? (
            <div className="py-20 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading countries…</p>
            </div>
          ) : (
            <DataTable
              data={filteredCountries}
              columns={columns}
              actions={actions}
              loading={false}
              searchable={true}
              filterable={false}
              sortable={true}
              selectable={false}
              pageSize={20}
              showPagination={true}
              searchPlaceholder="Search countries..."
              emptyMessage={
                statusFilter === 'active'
                  ? 'No active countries. Enable Ghana, United States, or Canada.'
                  : 'No countries found. Get started by creating a new country.'
              }
              className="!border-0 !shadow-none"
            />
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreateCountryModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateCountry}
          isLoading={createCountryMutation.isLoading}
        />
      )}

      {showEditModal && selectedCountry && (
        <CreateCountryModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedCountry(null);
          }}
          onSave={handleEditCountry}
          country={selectedCountry}
          isEdit={true}
          isLoading={updateCountryMutation.isLoading}
        />
      )}
    </div>
  );
};

export default AdminCountries;
